import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Save, 
  Plus, 
  Trash2, 
  ShoppingCart, 
  User, 
  Phone, 
  MapPin, 
  Calendar,
  Package,
  DollarSign,
  ArrowLeft,
  ChevronDown,
  Send,
  CheckCircle,
  X,
  ArrowRight
} from 'lucide-react';
import { Lead, Make, Model, ServiceType, CartItem, Order } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { LEAD_STATUSES } from '../constants/leadStatuses';

const EditLeadPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPage: number = (location.state as any)?.fromPage || 1;

  // Form state
  const [lead, setLead] = useState<Lead | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [languagePreferred, setLanguagePreferred] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [leadStatus, setLeadStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [notes, setNotes] = useState('');

  // Selection state
  const [makes, setMakes] = useState<Make[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [services, setServices] = useState<ServiceType[]>([]);
  
  const [selectedMake, setSelectedMake] = useState<number | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  
  // Cart state
  const [cart, setCart] = useState<CartItem[]>([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingLead, setLoadingLead] = useState(true);
  
  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Search state for autocomplete
  const [makeSearch, setMakeSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [showMakeDropdown, setShowMakeDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [filteredMakes, setFilteredMakes] = useState<Make[]>([]);
  const [filteredModels, setFilteredModels] = useState<Model[]>([]);

  useEffect(() => {
    const initializeData = async () => {
      await loadMakes();
      if (id) {
        await loadLead(parseInt(id));
      }
    };
    initializeData();
  }, [id]);

  // Filter makes based on search
  useEffect(() => {
    if (makeSearch) {
      const filtered = makes.filter(make => 
        make.name.toLowerCase().includes(makeSearch.toLowerCase())
      );
      setFilteredMakes(filtered);
    } else {
      setFilteredMakes(makes);
    }
  }, [makes, makeSearch]);

  // Filter models based on search
  useEffect(() => {
    if (modelSearch) {
      const filtered = models.filter(model => 
        model.name.toLowerCase().includes(modelSearch.toLowerCase())
      );
      setFilteredModels(filtered);
    } else {
      setFilteredModels(models);
    }
  }, [models, modelSearch]);

  const loadMakes = async () => {
    try {
      const response = await apiService.getMakes();
      if (response.isSuccess && response.entity) {
        setMakes(response.entity);
        setFilteredMakes(response.entity);
      }
    } catch (error) {
      console.error('Error loading makes:', error);
      toast.error('Failed to load makes');
    }
  };

  const loadModels = async (makeId: number) => {
    try {
      setSelectedModel(null);
      setModelSearch('');
      setServices([]);
      setCart([]);
      
      const response = await apiService.getModels(makeId);
      if (response.isSuccess && response.entity) {
        setModels(response.entity);
        setFilteredModels(response.entity);
      }
    } catch (error) {
      console.error('Error loading models:', error);
      toast.error('Error loading models');
    }
  };

  const loadServices = async (modelId: number, existingCart?: CartItem[]) => {
    try {
      // Don't clear cart here - we want to preserve existing cart items
      
      const response = await apiService.getServiceTypes(modelId);
      if (response.isSuccess && response.entity) {
        setServices(response.entity);
        
        // Try to match cart items with services by name and update service IDs
        const cartToUpdate = existingCart || cart;
        if (cartToUpdate.length > 0) {
          const updatedCart = cartToUpdate.map(cartItem => {
            const matchingService = response.entity.find(
              service => service.serviceName === cartItem.serviceName
            );
            if (matchingService) {
              return { ...cartItem, id: matchingService.id };
            }
            return cartItem;
          });
          setCart(updatedCart);
        }
      }
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Error loading services');
    }
  };

  // Helper function to convert API cart items to CartItem format
  const convertCartItems = (cartItems: any[], makeId: number = 0, makeName: string = '', modelId: number = 0, modelName: string = ''): CartItem[] => {
    return cartItems.map((item: any) => ({
      // Use serviceId if available, otherwise use the cart item's original ID to ensure uniqueness
      id: item.serviceId && item.serviceId !== 0 ? item.serviceId : (item.id || 0),
      serviceName: item.serviceName,
      actualPrice: item.actualPrice,
      serviceCost: item.serviceCost,
      quantity: item.quantity || 1,
      serviceType: item.serviceType || 'Repair',
      warrantyPeriod: item.warrantyPeriod || 90,
      modelId: modelId,
      serviceImage: item.serviceImage || '',
      serviceDuration: 60,
      marginalCost: item.marginalCost || 0,
      markedPrice: item.actualPrice?.toString() || '0',
      makeId: makeId,
      makeName: makeName,
      modelName: modelName,
      warrantyCoveredItems: item.warrantyCoveredItems || '',
      warrantyNotCoveredItems: item.warrantyNotCoveredItems || '',
      warrantyTypeDescription: item.warrantyTypeDescription || '',
      warrantyName: item.warrantyName || '',
      warrantyTypeId: item.warrantyTypeId || 1,
      warrantyEndDate: item.warrantyEndDate || new Date()
    }));
  };

  const loadLead = async (leadId: number) => {
    try {
      setLoadingLead(true);
      const response = await apiService.getLead(leadId);
      if (response.isSuccess && response.entity) {
        const leadData = response.entity;
        setLead(leadData);
        setCustomerName(leadData.name);
        setCustomerMobile(leadData.mobile);
        setCustomerEmail(leadData.email || '');
        setCustomerAddress(leadData.address || '');
        setLanguagePreferred(leadData.languagePreferred || '');
        setLeadSource(leadData.source);
        setLeadStatus(leadData.status || 'New');
        setPriority(leadData.priority);
        setNotes(leadData.notes || '');
        setDiscountAmount(leadData.discountAmount || 0);
        
        // Load cart first (always load cart if available)
        if (leadData.cart && leadData.cart.length > 0) {
          // We'll update cart with proper make/model IDs after they're loaded
          const tempCartItems = convertCartItems(
            leadData.cart,
            0,
            leadData.deviceType || '',
            0,
            leadData.deviceModel || ''
          );
          setCart(tempCartItems);
        }
        
        // Ensure makes are loaded before trying to match
        let makesToUse = makes;
        if (makesToUse.length === 0) {
          const makesResponse = await apiService.getMakes();
          if (makesResponse.isSuccess && makesResponse.entity) {
            makesToUse = makesResponse.entity;
            setMakes(makesResponse.entity);
            setFilteredMakes(makesResponse.entity);
          }
        }
        
        // Set make and model if available
        if (leadData.deviceType && makesToUse.length > 0) {
          // Try case-sensitive match first, then case-insensitive, then trimmed
          const deviceTypeTrimmed = leadData.deviceType.trim();
          let make = makesToUse.find(m => m.name === deviceTypeTrimmed);
          if (!make) {
            make = makesToUse.find(m => m.name.toLowerCase() === deviceTypeTrimmed.toLowerCase());
          }
          if (!make) {
            // Try matching with trimmed make names
            make = makesToUse.find(m => m.name.trim().toLowerCase() === deviceTypeTrimmed.toLowerCase());
          }
          
          if (make) {
            console.log('Found make:', make.name, 'for deviceType:', leadData.deviceType);
            setSelectedMake(make.id);
            setMakeSearch(make.name);
            const modelsResponse = await apiService.getModels(make.id);
            if (modelsResponse.isSuccess && modelsResponse.entity) {
              setModels(modelsResponse.entity);
              setFilteredModels(modelsResponse.entity);
              
              // Set model after models are loaded
              if (leadData.deviceModel) {
                // Try case-sensitive match first, then case-insensitive, then trimmed
                const deviceModelTrimmed = leadData.deviceModel.trim();
                let model = modelsResponse.entity.find(m => m.name === deviceModelTrimmed);
                if (!model) {
                  model = modelsResponse.entity.find(m => m.name.toLowerCase() === deviceModelTrimmed.toLowerCase());
                }
                if (!model) {
                  // Try matching with trimmed model names
                  model = modelsResponse.entity.find(m => m.name.trim().toLowerCase() === deviceModelTrimmed.toLowerCase());
                }
                if (model) {
                  console.log('Found model:', model.name, 'for deviceModel:', leadData.deviceModel);
                  setSelectedModel(model);
                  setModelSearch(model.name);
                  
                  // Update cart with proper make/model IDs before loading services
                  let cartItems: CartItem[] = [];
                  if (leadData.cart && leadData.cart.length > 0) {
                    cartItems = convertCartItems(
                      leadData.cart,
                      make.id,
                      make.name,
                      model.id,
                      model.name
                    );
                    setCart(cartItems);
                  }
                  
                  // Load services and match cart items
                  await loadServices(model.id, cartItems);
                } else {
                  console.warn('Model not found:', leadData.deviceModel, 'Available models:', modelsResponse.entity.map(m => m.name));
                  // Model not found, but update cart with make info
                  if (leadData.cart && leadData.cart.length > 0) {
                    const cartItems = convertCartItems(
                      leadData.cart,
                      make.id,
                      make.name,
                      0,
                      leadData.deviceModel
                    );
                    setCart(cartItems);
                  }
                }
              } else {
                // No model, but update cart with make info
                if (leadData.cart && leadData.cart.length > 0) {
                  const cartItems = convertCartItems(
                    leadData.cart,
                    make.id,
                    make.name,
                    0,
                    ''
                  );
                  setCart(cartItems);
                }
              }
            }
          } else {
            console.warn('Make not found:', leadData.deviceType, 'Available makes:', makesToUse.map(m => m.name));
          }
        }
      } else {
        toast.error('Lead not found');
        navigate('/leads', { state: { page: fromPage } });
      }
    } catch (error) {
      console.error('Error loading lead:', error);
      toast.error('Failed to load lead');
      navigate('/leads', { state: { page: fromPage } });
    } finally {
      setLoadingLead(false);
    }
  };

  const handleMakeSelect = (make: Make) => {
    setSelectedMake(make.id);
    setMakeSearch(make.name);
    setShowMakeDropdown(false);
    setSelectedModel(null);
    setModelSearch('');
    setServices([]);
    setCart([]);
    setTimeout(() => {
      loadModels(make.id);
    }, 0);
  };

  const handleModelSelect = (model: Model) => {
    setSelectedModel(model);
    setModelSearch(model.name);
    setShowModelDropdown(false);
    setTimeout(() => {
      loadServices(model.id);
    }, 0);
  };

  const addToCart = (service: ServiceType) => {
    const existingItem = cart.find(item => item.id === service.id);
    if (existingItem) {
      removeFromCart(service.id);
      return;
    }
    setCart([...cart, { ...service, quantity: 1 }]);
    toast.success('Service added to cart');
  };

  const removeFromCart = (serviceId: number) => {
    setCart(cart.filter(item => item.id !== serviceId));
    toast.success('Service removed from cart');
  };

  const updateQuantity = (serviceId: number, quantity: number) => {
    if (quantity < 1) return;
    setCart(cart.map(item => 
      item.id === serviceId ? { ...item, quantity } : item
    ));
  };

  const calculateTotals = () => {
    const totalItemPrice = cart.reduce((sum, item) => sum + (item.actualPrice * item.quantity), 0);
    const totalServiceCost = cart.reduce((sum, item) => sum + (item.serviceCost * item.quantity), 0);
    const serviceCostAfterDiscount = Math.max(totalServiceCost - discountAmount, 0);
    const gstAmount = (serviceCostAfterDiscount * 18) / 100;
    const finalAmount = totalItemPrice + serviceCostAfterDiscount + gstAmount;
    
    return {
      totalItemPrice,
      totalServiceCost,
      gstAmount,
      finalAmount
    };
  };

  const handleUpdateLead = async (showModal: boolean = true) => {
    if (!lead) return;
    
    setLoading(true);
    try {
      const totals = calculateTotals();
      
      const leadData: Lead = {
        ...lead,
        name: customerName,
        mobile: customerMobile,
        email: customerEmail || '',
        address: customerAddress || '',
        languagePreferred: languagePreferred || '',
        source: leadSource,
        status: leadStatus || 'New',
        priority,
        notes,
        deviceType: selectedMake ? makes.find(m => m.id === selectedMake)?.name || '' : '',
        deviceModel: selectedModel ? selectedModel.name : '',
        serviceRequired: cart.length > 0 ? cart.map(item => item.serviceName).join(', ') : '',
        cart: cart,
        totalItemPrice: totals.totalItemPrice,
        totalServiceCost: totals.totalServiceCost,
        discountAmount,
        finalAmount: totals.finalAmount,
        updatedAt: new Date().toISOString(),
        updatedBy: 1
      };

      const response = await apiService.updateLead(lead.id, leadData);
      
      // Only show modal if explicitly requested and API call was successful
      if (response.isSuccess) {
        // Update the lead state with the latest data
        setLead(leadData);
        
        if (showModal) {
          setSuccessMessage('Lead updated successfully');
          setShowSuccessModal(true);
        }
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to update lead');
      }
      
      return response;
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Error updating lead');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSendQuotation = async () => {
    if (!lead) return;
    
    if (cart.length === 0) {
      toast.error('Please add at least one service to send quotation');
      return;
    }
    
    setLoading(true);
    try {
      // First update the lead with cart data (without showing modal)
      await handleUpdateLead(false);
      
      // Call the send quotation API and wait for response
      const response = await apiService.sendQuotation(lead.id);
      
      // Show modal only after receiving successful API response
      if (response.isSuccess) {
        setLeadStatus('Quotation Send');
        setLead(prev => prev ? { ...prev, status: 'Quotation Send' } : prev);
        setSuccessMessage('Quotation sent successfully');
        setShowSuccessModal(true);
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to send quotation');
      }
    } catch (error) {
      console.error('Error sending quotation:', error);
      toast.error('Error sending quotation');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToOrder = async () => {
    if (!lead) return;
    
    if (cart.length === 0) {
      toast.error('Please add at least one service to convert to order');
      return;
    }
    
    if (!selectedMake || !selectedModel) {
      toast.error('Please select Make and Model before converting to order');
      return;
    }
    
    setLoading(true);
    try {
      const totals = calculateTotals();
      
      // Convert cart items to orderItems format
      const orderItems = cart.map((item) => ({
        id: 0, // Will be set by backend
        orderId: 0, // Will be set by backend
        serviceId: item.id,
        serviceName: item.serviceName,
        serviceType: item.serviceType || 'Repair',
        actualPrice: item.actualPrice,
        serviceCost: item.serviceCost,
        quantity: item.quantity,
        itemTotal: item.actualPrice * item.quantity,
        serviceStatus: 'Pending',
        notes: '',
        warrantyPeriod: item.warrantyPeriod || 90,
        warrantyEndDate: item.warrantyEndDate || new Date(Date.now() + (item.warrantyPeriod || 90) * 24 * 60 * 60 * 1000),
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 1,
        warrantyTypeId: item.warrantyTypeId || 1,
        warrantyName: item.warrantyName || '',
        warrantyTypeDescription: item.warrantyTypeDescription || '',
        warrantyCoveredItems: item.warrantyCoveredItems || '',
        warrantyNotCoveredItems: item.warrantyNotCoveredItems || '',
        serviceImage: item.serviceImage || ''
      }));
      
      const orderData: Partial<Order> = {
        customerName,
        customerMobile,
        customerEmail: customerEmail || '',
        customerAddress: customerAddress || '',
        makeId: selectedMake,
        makeName: makes.find(m => m.id === selectedMake)?.name || '',
        modelId: selectedModel.id,
        modelName: selectedModel.name,
        orderDate: new Date(),
        orderStatus: 'Created',
        totalItemPrice: totals.totalItemPrice,
        totalServiceCost: totals.totalServiceCost,
        discountAmount,
        gstPercentage: 18,
        gstAmount: totals.gstAmount,
        finalAmount: totals.finalAmount,
        paymentStatus: 'Pending',
        paymentMethod: 'Cash',
        amountPaid: 0,
        balanceAmount: totals.finalAmount,
        technicianId: 0,
        notes: notes || '',
        deviceCondition: 'Good',
        warrantyPeriod: 90,
        warrantyEndDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
        createdBy: 1,
        updatedBy: 1,
        isActive: true,
        orderItems: orderItems
      };

      const response = await apiService.createOrder(orderData as Order);
      
      if (response.isSuccess) {
        toast.success('Lead converted to order successfully');
        navigate('/orders');
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to convert lead to order');
      }
    } catch (error) {
      console.error('Error converting lead to order:', error);
      toast.error('Error converting lead to order');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  if (loadingLead) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Lead not found</h3>
        <button
          onClick={() => navigate('/leads', { state: { page: fromPage } })}
          className="text-indigo-600 hover:text-indigo-700"
        >
          Back to Leads
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/leads', { state: { page: fromPage } })}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <User className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Edit Lead</h1>
              <p className="text-sm text-gray-500">Lead ID: {id}</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleConvertToOrder}
            disabled={loading || cart.length === 0}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Convert to Order
          </button>
          <button
            onClick={handleSendQuotation}
            disabled={loading || cart.length === 0}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 rounded-lg hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4 mr-2" />
            Send Quotation
          </button>
          <button
            onClick={() => handleUpdateLead(true)}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-lg hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Update Lead
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Customer & Product Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
                <input
                  type="tel"
                  value={customerMobile}
                  onChange={(e) => setCustomerMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit mobile number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Language Preferred</label>
                <select
                  value={languagePreferred}
                  onChange={(e) => setLanguagePreferred(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select Language</option>
                  <option value="Hindi">Hindi</option>
                  <option value="English">English</option>
                  <option value="Marathi">Marathi</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <textarea
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="Enter customer address"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Services Selection */}
          {selectedModel && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="border-b border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Available Services</h3>
                      <p className="text-sm text-gray-500">Select services for {selectedModel.name}</p>
                    </div>
                  </div>
                  <div className="bg-blue-50 px-3 py-1 rounded-full">
                    <span className="text-sm font-medium text-blue-700">{services.length} available</span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {services.map((service) => {
                    const isInCart = cart.some(item => item.id === service.id);
                    return (
                      <div 
                        key={service.id} 
                        className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                          isInCart 
                            ? 'border-blue-500 bg-blue-50 shadow-sm' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => addToCart(service)}
                      >
                        <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                          {service.serviceImage ? (
                            <img
                              src={apiService.getFullImageUrl(service.serviceImage)}
                              alt={service.serviceName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full flex items-center justify-center ${service.serviceImage ? 'hidden' : ''}`}>
                            <Package className="h-6 w-6 text-gray-400" />
                          </div>
                        </div>
                        
                        <div className="ml-4 flex-1 min-w-0">
                          <h4 className="text-base font-semibold text-gray-900 truncate">
                            {service.serviceName}
                          </h4>
                          <div className="mt-1 flex items-center space-x-4">
                            <span className="text-lg font-bold text-blue-600">
                              ₹{Math.round(service.actualPrice + (service.serviceCost || 0) * 1.18).toLocaleString()}
                            </span>
                            <span className="text-sm text-gray-500">
                              Service Cost: ₹{service.serviceCost.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        
                        <div className="ml-4 flex-shrink-0">
                          {isInCart ? (
                            <div className="flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span className="text-sm font-medium">Added</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                              <Plus className="w-4 h-4" />
                              <span className="text-sm font-medium">Add</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {services.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Package className="h-8 w-8 text-gray-400" />
                    </div>
                    <h4 className="text-lg font-medium text-gray-600 mb-2">No Services Available</h4>
                    <p className="text-gray-500">No services are currently available for this model</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lead Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Lead Details</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lead Source</label>
                <select
                  value={leadSource}
                  onChange={(e) => setLeadSource(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="Website">Website</option>
                  <option value="Phone">Phone</option>
                  <option value="Email">Email</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Referral">Referral</option>
                  <option value="Walk-in">Walk-in</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lead Status</label>
                <select
                  value={leadStatus}
                  onChange={(e) => setLeadStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {leadStatus && !LEAD_STATUSES.includes(leadStatus as any) && (
                    <option value={leadStatus}>{leadStatus}</option>
                  )}
                  {LEAD_STATUSES.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Enter any additional notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Device Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Package className="h-4 w-4 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Device Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                <div className="relative">
                  <input
                    type="text"
                    value={makeSearch}
                    onChange={(e) => {
                      setMakeSearch(e.target.value);
                      setShowMakeDropdown(true);
                    }}
                    onFocus={() => setShowMakeDropdown(true)}
                    onClick={() => {
                      setShowMakeDropdown(true);
                      if (!makeSearch) {
                        setFilteredMakes(makes);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowMakeDropdown(false), 300)}
                    placeholder="Search and select make..."
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                  {showMakeDropdown && filteredMakes.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredMakes.map((make) => (
                        <button
                          key={make.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleMakeSelect(make);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-blue-50"
                        >
                          <div className="flex items-center space-x-3">
                            {make.imagePath && (
                              <img
                                src={apiService.getFullImageUrl(make.imagePath)}
                                alt={make.name}
                                className="w-6 h-6 rounded object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            )}
                            <span className="font-medium">{make.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                <div className="relative">
                  <input
                    type="text"
                    value={modelSearch}
                    onChange={(e) => {
                      setModelSearch(e.target.value);
                      setShowModelDropdown(true);
                    }}
                    onFocus={() => setShowModelDropdown(true)}
                    onClick={() => {
                      if (selectedMake) {
                        setShowModelDropdown(true);
                        if (!modelSearch) {
                          setFilteredModels(models);
                        }
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowModelDropdown(false), 300)}
                    placeholder="Search and select model..."
                    disabled={!selectedMake}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className={`h-4 w-4 ${selectedMake ? 'text-gray-400' : 'text-gray-300'}`} />
                  </div>
                  {showModelDropdown && filteredModels.length > 0 && selectedMake && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredModels.map((model) => (
                        <button
                          key={model.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleModelSelect(model);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-blue-50"
                        >
                          <div className="flex items-center space-x-3">
                            {model.imagePath && (
                              <img
                                src={apiService.getFullImageUrl(model.imagePath)}
                                alt={model.name}
                                className="w-6 h-6 rounded object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            )}
                            <span className="font-medium">{model.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Cart & Summary */}
        <div className="space-y-6">
          {/* Shopping Cart */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="h-4 w-4 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Service Cart</h3>
            </div>
            
            {cart.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No services selected</p>
            ) : (
              <div className="space-y-4">
                {cart.map((item, index) => (
                  <div key={`${item.id}-${item.serviceName}-${index}`} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{item.serviceName}</h4>
                      <p className="text-sm text-gray-500">₹{Math.round(item.actualPrice + (item.serviceCost || 0) * 1.18).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                      />
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quotation Summary */}
          {cart.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quotation Summary</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Item Total</span>
                  <span>₹{(totals.totalItemPrice || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Service Cost</span>
                  <span>₹{(totals.totalServiceCost || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST (18%)</span>
                  <span>₹{(totals.gstAmount || 0).toLocaleString()}</span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount</label>
                  <input
                    type="number"
                    min="0"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Final Amount</span>
                    <span>₹{(totals.finalAmount || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={() => setShowSuccessModal(false)}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Success
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {successMessage}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditLeadPage;

